import { extendObservable, action } from 'mobx';
import PropTypes from 'prop-types';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import moment from 'moment';
import { apiCall } from '../../API';
import filterOutDeletedPolicyCategories from '../filterOutDeletedPolicyCategories';
import AsyncActionStore from '../AsyncActionStore';
import FlagStore from '../FlagStore';
import MenuStore from '../MenuStore';
import makeDefaultUDPolicy from './makeDefaultUDPolicy';
import UDPolicyEditStore from './UDPolicyEditStore';

export default class UDPoliciesStore {
    static PropType = PropTypes.shape({
        load: PropTypes.func.isRequired,
        menu: MenuStore.PropType.isRequired,
        setOrderStatus: AsyncActionStore.PropType.isRequired,
        reordering: PropTypes.bool.isRequired,
        originalOrder: MobxPropTypes.arrayOrObservableArray,
        newOrder: MobxPropTypes.arrayOrObservableArray,
        canChangeOrder: PropTypes.bool.isRequired,
        editStore: UDPolicyEditStore.PropType.isRequired,
        createStatus: AsyncActionStore.PropType.isRequired,
        updateStatus: AsyncActionStore.PropType.isRequired,
        showDeleteModal: PropTypes.func.isRequired,
        deletePolicy: PropTypes.func.isRequired,
        deleteStatus: AsyncActionStore.PropType.isRequired,
        deleteModal: FlagStore.PropType.isRequired,
        deleteConfirmation: FlagStore.PropType.isRequired,
        loadLastSyncStatus: AsyncActionStore.PropType,
        lastSyncGroup: PropTypes.number,
        loadLastSyncGroup: PropTypes.func,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class UDPoliciesStore */{
            defaultForbiddenCategories: [],
            defaultIsolatedCategories: [],

            apiList: apiCall('/console/url_isolation_policy/list', { isUserAction: false }),
            listStatus: new AsyncActionStore(),
            menu: new MenuStore(),
            defaultPolicyId: null,

            // editing priorities
            apiSetOrder: apiCall('/console/url_isolation_policy/set_priorities'),
            setOrderStatus: new AsyncActionStore(),
            reordering: false,
            originalOrder: [],
            newOrder: [],
            get canChangeOrder() {
                if (this.defaultPolicyId) {
                    return this.originalOrder.length > 2;
                }
                return this.originalOrder.length > 1;
            },

            // editing one policy
            editStore: new UDPolicyEditStore(),
            apiCreate: apiCall('/console/url_isolation_policy/create'),
            createStatus: new AsyncActionStore(),
            apiUpdate: apiCall('/console/url_isolation_policy/update'),
            updateStatus: new AsyncActionStore(),

            // deleting policy
            apiDelete: apiCall('/console/url_isolation_policy/delete'),
            deleteStatus: new AsyncActionStore(),
            deleteModal: new FlagStore(false),
            deleteConfirmation: new FlagStore(false),

            loadLastSyncStatus: new AsyncActionStore(),
            apiLoadLastSync: apiCall('/console/usergroups/cloudsync/updateTime?alwaysDisplay=true'),
            lastSyncGroupDate: null,
        }, data);
    }

    makePlainPolicyObject = (customerPolicy) => ({
        ...makeDefaultUDPolicy(this.defaultForbiddenCategories, this.defaultIsolatedCategories),
        ...customerPolicy.policy,
        id: customerPolicy.id,
        isDefault: customerPolicy.id === this.defaultPolicyId,
    });

    setPolicies = action((policies) => {
        this.originalOrder.replace(policies);
        this.newOrder.replace(this.originalOrder);
        return policies;
    });

    // create
    showCreate = action(() => {
        this.editStore.setValue(this.makePlainPolicyObject({ id: null }));
        this.editStore.expandAll();
        this.editStore.viewMode = false;
        this.editStore.modal.open();
    });

    createPolicy = action(() => {
        if (!this.createStatus.isLoading) {
            const policyData = this.editStore.getValue();

            if (this.editStore.nameStore.isEmpty) {
                this.editStore.nameStore.setTouched(false);
                this.editStore.nameStore.focus();
                return Promise.reject();
            }

            policyData.type = 'URLIsolationPolicy';
            this.createStatus.setLoading(this.apiCreate(policyData)
                .then(action((customerPolicy) => {
                    const newPolicy = this.makePlainPolicyObject(customerPolicy);
                    const policies = this.originalOrder.slice();
                    if (this.defaultPolicyId) {
                        policies.splice(-1, 0, newPolicy);
                    } else {
                        policies.push(newPolicy);
                    }
                    this.setPolicies(policies);
                    this.editStore.modal.close();
                    return newPolicy;
                })));
        }
        return this.createStatus.promise;
    });

    // read
    load = action(() => {
        if (!this.listStatus.isLoading) {
            this.listStatus.setLoading(this.apiList()
                .then(({ policies, defaultPolicyId, userGroups, userGroupsError, categories }) => {
                    this.defaultPolicyId = defaultPolicyId;
                    this.editStore.setMeta(userGroups, userGroupsError, categories);

                    filterOutDeletedPolicyCategories(policies, categories);

                    policies = policies.map(this.makePlainPolicyObject);
                    this.setPolicies(policies);
                }));
        }
        return this.listStatus.promise;
    });

    loadLastSyncGroup = () => {
        if (!this.loadLastSyncStatus.isLoading) {
            this.loadLastSyncStatus.setLoading(this.apiLoadLastSync().then((lastSync) => {
                if (!lastSync) {
                    this.lastSyncGroupDate = '';
                } else {
                    const date = moment.utc(lastSync);
                    const daysDiff = moment.utc().diff(date, 'days');
                    this.lastSyncGroupDate = daysDiff <= 30 ?
                        date.format('MMM/DD/YYYY [at] hh:mma [(GMT)]') : 'More than 30 Days ago';
                }
            }));
        }
        return this.loadLastSyncStatus.promise;
    }

    // update
    showUpdate = action((customerPolicy) => {
        this.menu.close();
        this.editStore.setValue(customerPolicy);
        this.editStore.expandAll();
        this.editStore.viewMode = false;
        this.editStore.modal.open();
    });

    showView = action((customerPolicy) => {
        this.menu.close();
        this.editStore.setValue(customerPolicy);
        this.editStore.expandAll();
        this.editStore.viewMode = true;
        this.editStore.modal.open();
    });

    updatePolicy = action(() => {
        if (!this.updateStatus.isLoading) {
            const id = this.editStore.id;
            const policy = this.editStore.getValue();
            policy.type = 'URLIsolationPolicy';

            if (this.editStore.nameStore.isEmpty) {
                this.editStore.nameStore.setTouched(false);
                this.editStore.nameStore.focus();
                return Promise.reject();
            }

            this.updateStatus.setLoading(this.apiUpdate({ id, policy })
                .then(action((newPolicy) => {
                    this.originalOrder.splice(
                        this.originalOrder.findIndex((p) => p.id === id),
                        1,
                        this.makePlainPolicyObject(newPolicy),
                    );
                    this.setPolicies([...this.originalOrder]);
                    this.editStore.modal.close();
                    return newPolicy;
                })));
        }
        return this.updateStatus.promise;
    });

    // reordering
    startReordering = action(() => {
        this.reordering = true;
        this.newOrder.replace(this.originalOrder);
    });

    cancelReordering = action(() => {
        this.reordering = false;
        this.newOrder.replace(this.originalOrder);
    });

    saveOrder = action(() => {
        if (this.newOrder.every((p, i) => this.originalOrder[i].id === p.id)) {
            this.reordering = false;
            return Promise.resolve();
        }

        if (!this.setOrderStatus.isLoading) {
            this.setOrderStatus.setLoading(this.apiSetOrder(this.newOrder.map((p) => p.id))
                .then(action(() => {
                    this.originalOrder.replace(this.newOrder);
                    this.reordering = false;
                })));
        }
        return this.setOrderStatus.promise;
    });

    canIncreasePriority = (p) => !p.isDefault && (p !== this.newOrder[0]);

    canLowerPriority = (p) => !p.isDefault
        && (p !== this.newOrder[this.newOrder.length - 1])
        && (!this.defaultPolicyId || p !== this.newOrder[this.newOrder.length - 2]);

    orderUp = action((p) => {
        const i = this.newOrder.indexOf(p);
        if ((i !== -1) && (i !== 0)) {
            const newOrder = [...this.newOrder];
            const prev = this.newOrder[i - 1];
            newOrder[i - 1] = p;
            newOrder[i] = prev;
            this.newOrder.replace(newOrder);
        }
    });

    orderDown = action((p) => {
        const i = this.newOrder.indexOf(p);
        if ((i !== -1) && (i < this.newOrder.length - 1)) {
            const newOrder = [...this.newOrder];
            const next = this.newOrder[i + 1];
            newOrder[i + 1] = p;
            newOrder[i] = next;
            this.newOrder.replace(newOrder);
        }
    });

    orderTop = action((p) => {
        const i = this.newOrder.indexOf(p);
        if ((i !== -1) && this.canIncreasePriority(p)) {
            this.newOrder.remove(p);
            this.newOrder.splice(0, 0, p);
        }
    });

    orderBottom = action((p) => {
        const i = this.newOrder.indexOf(p);
        if ((i !== -1) && this.canLowerPriority(p)) {
            this.newOrder.remove(p);
            this.newOrder.splice(this.newOrder.length - (this.defaultPolicyId ? 1 : 0), 0, p);
        }
    });

    // deleting
    showDeleteModal = action((policy) => {
        this.menu.close();
        this.deleteConfirmation.setFalse();
        this.editStore.setValue(policy);
        this.deleteModal.open();
    });

    deletePolicy = action(() => {
        if (!this.deleteStatus.isLoading) {
            const targetPolicy = this.originalOrder.find((p) => p.id === this.editStore.id);
            this.deleteStatus.setLoading(this.apiDelete({ id: targetPolicy.id })
                .then(action(() => {
                    this.originalOrder.remove(targetPolicy);
                    this.newOrder.remove(targetPolicy);
                    this.deleteModal.close();
                    return targetPolicy;
                })));
        }
        return this.deleteStatus.promise;
    });
}
